import { Empty } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { ArrowRightIcon, BicycleIcon, ClockIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ReadFailedCard } from "@bednbody/ui/components/read-failed-card";
import { minutesSince } from "@/lib/time";
import type { CommandBooking, useDispatchQueue } from "../command-center.hooks";

type QueueRows = NonNullable<ReturnType<typeof useDispatchQueue>["data"]>;

interface DispatchQueueCardProps {
  bookings: CommandBooking[];
  queue: QueueRows;
  isLoading: boolean;
  /** A failed read is not an empty queue — see ReadFailedCard. */
  failed?: boolean;
  /** false for read-only roles (executive) that can't visit /dispatch. */
  canDispatch: boolean;
}

/**
 * Bookings waiting for dispatch (oldest first) next to the rider FIFO head.
 * Read-only by design: assignment runs through the dispatch Edge Function
 * (assign_rider is service_role-only, 0006), so actions deep-link to
 * /dispatch instead of duplicating that flow here. There is no priority
 * column in the schema — the queue is FIFO, so "priority" here is simply age.
 */
export function DispatchQueueCard({
  bookings,
  queue,
  isLoading,
  failed = false,
  canDispatch,
}: DispatchQueueCardProps) {
  const waiting = useMemo(
    () =>
      bookings
        .filter((b) => b.status === "pending" || b.status === "awaiting_rider")
        .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)),
    [bookings],
  );
  const availableRiders = queue.filter((r) => r.status === "available");

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  // Claimed before anything renders, including the rider count: "Riders
  // available now: 0" over "Nothing waiting for dispatch 🎉" is two lies
  // agreeing with each other, which is far more convincing than one.
  if (failed) {
    return (
      <ReadFailedCard message="The dispatch queue and today's bookings could not be read, so this is not a picture of the floor." />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl bg-background px-3 py-2 text-sm">
        <span className="flex items-center gap-2 text-muted-foreground">
          <BicycleIcon size={14} className="text-primary" />
          Riders available now
        </span>
        <span className="font-semibold tabular-nums text-foreground">{availableRiders.length}</span>
      </div>

      {waiting.length === 0 ? (
        <Empty>Nothing waiting for dispatch. 🎉</Empty>
      ) : (
        <ul className="space-y-2">
          {waiting.slice(0, 5).map((b) => {
            const late = minutesSince(b.scheduled_at);
            return (
              <li
                key={b.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {b.ref}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {b.hubs?.name ?? ""}
                    </span>
                  </p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <ClockIcon size={11} />
                    {new Date(b.scheduled_at).toLocaleTimeString("en-PH", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {late > 0 ? (
                      <span className="font-medium text-destructive">· {late}m overdue</span>
                    ) : null}
                    <span className="capitalize">· {b.status.replace(/_/g, " ")}</span>
                  </p>
                </div>
                {canDispatch && (
                  <Link
                    to="/dispatch"
                    className="shrink-0 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    Assign
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {waiting.length > 5 &&
        (canDispatch ? (
          <Link
            to="/dispatch"
            className="flex items-center justify-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            {waiting.length - 5} more in queue <ArrowRightIcon size={12} />
          </Link>
        ) : (
          <p className="text-center text-xs text-muted-foreground">
            {waiting.length - 5} more in queue
          </p>
        ))}
    </div>
  );
}
